import type { JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/features/auth/session';
import {
  ASSESSMENT_QUESTIONS,
  recommendMethod,
} from '@/features/students/preferred-method-assessment';
import {
  PREFERRED_METHOD_DESCRIPTIONS,
  PREFERRED_METHOD_LABELS,
  PREFERRED_METHOD_OPTIONS,
} from '@/features/students/preferred-method-labels';
import { useUpdatePreferredMethod } from '@/features/students/hooks';
import type { PreferredMemorizationMethod, StudentView } from '@/features/students/types';
import { useHasCapability } from '@/lib/capabilities';
import { ApiError } from '@/lib/api/client';

/**
 * Phase 8, Pilar A: the student's chosen memorization method, with an
 * optional 3-question assessment that only ever *suggests* an answer - the
 * student (or a Teacher/Admin helping them) still picks and saves
 * explicitly, nothing is written from the quiz alone. Edit controls are
 * shown to a `students:manage` holder or to the student themselves (the
 * same UX-gate shape `MessageThreadCard` uses for `messages:manage`); the
 * server performs the real authorization either way (student.service.ts's
 * callerMayWritePreferredMethod), so a Teacher with no active class
 * assignment still sees the form but gets a rejected save.
 */
export function PreferredMethodCard({ student }: { readonly student: StudentView }): JSX.Element {
  const { user } = useAuth();
  const canManage = useHasCapability('students:manage');
  const isSelf = user?.userId === student.userId;
  const canEdit = canManage || isSelf;

  const [selected, setSelected] = useState<PreferredMemorizationMethod | null>(
    student.preferredMethod,
  );
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<
    Partial<Record<string, PreferredMemorizationMethod>>
  >({});
  const update = useUpdatePreferredMethod(student.id);

  const answeredValues = Object.values(quizAnswers).filter(
    (value): value is PreferredMemorizationMethod => value !== undefined,
  );
  const recommendation =
    answeredValues.length === ASSESSMENT_QUESTIONS.length ? recommendMethod(answeredValues) : null;

  function handleSave(): void {
    if (selected === null) {
      return;
    }
    update.mutate({ preferredMethod: selected });
  }

  const apiErrorMessage =
    update.error instanceof ApiError
      ? update.error.message
      : update.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Metode Hafalan</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {student.preferredMethod === null ? (
          <p className="text-sm text-muted-foreground">Belum ada metode yang dipilih.</p>
        ) : (
          <div>
            <p className="text-sm font-medium">
              {PREFERRED_METHOD_LABELS[student.preferredMethod]}
            </p>
            <p className="text-xs text-muted-foreground">
              {PREFERRED_METHOD_DESCRIPTIONS[student.preferredMethod]}
            </p>
          </div>
        )}

        {canEdit && (
          <div className="flex flex-col gap-3 border-t border-border pt-3">
            <div className="flex flex-col gap-2">
              <Select
                {...(selected !== null && { value: selected })}
                onValueChange={(value) => {
                  setSelected(value as PreferredMemorizationMethod);
                }}
              >
                <SelectTrigger aria-label="Pilih metode hafalan">
                  <SelectValue placeholder="Pilih metode..." />
                </SelectTrigger>
                <SelectContent>
                  {PREFERRED_METHOD_OPTIONS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {PREFERRED_METHOD_LABELS[method]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected !== null && (
                <p className="text-xs text-muted-foreground">
                  {PREFERRED_METHOD_DESCRIPTIONS[selected]}
                </p>
              )}
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-fit"
              onClick={() => {
                setShowQuiz((current) => !current);
              }}
            >
              {showQuiz ? 'Sembunyikan bantuan memilih' : 'Belum yakin? Bantu saya memilih'}
            </Button>

            {showQuiz && (
              <div className="flex flex-col gap-4 rounded-md border border-border p-3">
                {ASSESSMENT_QUESTIONS.map((question) => (
                  <div key={question.id} className="flex flex-col gap-2">
                    <p className="text-sm font-medium">{question.prompt}</p>
                    <div className="flex flex-col gap-1">
                      {question.options.map((option) => (
                        <Button
                          key={option.label}
                          type="button"
                          size="sm"
                          variant={
                            quizAnswers[question.id] === option.weight ? 'default' : 'outline'
                          }
                          className="h-auto justify-start whitespace-normal text-left"
                          onClick={() => {
                            setQuizAnswers((current) => ({
                              ...current,
                              [question.id]: option.weight,
                            }));
                          }}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}

                {recommendation !== null && (
                  <Alert>
                    <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        Rekomendasi: <strong>{PREFERRED_METHOD_LABELS[recommendation]}</strong>
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          setSelected(recommendation);
                        }}
                      >
                        Gunakan rekomendasi ini
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {apiErrorMessage !== null && (
              <Alert variant="destructive">
                <AlertDescription>{apiErrorMessage}</AlertDescription>
              </Alert>
            )}

            <Button
              type="button"
              size="sm"
              className="w-fit"
              disabled={
                selected === null || selected === student.preferredMethod || update.isPending
              }
              onClick={handleSave}
            >
              {update.isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
